<template>
  <HpSidebar :id="id">
    <aside class="flex flex-col items-center gap-2.5 pb-4 text-center">
      <header class="flex w-full flex-col items-center gap-2 pb-7 pt-6">
        <h2 class="text-3xl">{{ content.data.title }}</h2>
        <template v-if="benchmark">
          <QuoteCostMeter :benchmark="benchmark" wide class="my-3" />
          <QuoteBenchmark
            v-if="quotingConfig.smartCardShowBenchmarks"
            id="healthcare_costs"
            :benchmark="benchmark"
          />
        </template>
      </header>

      <h3 class="max-w-sm text-3xl leading-tight" v-html="content.data.heading"></h3>
      <p class="max-w-xs text-balance text-lg leading-7">{{ content.data.text }}</p>

      <section
        class="my-4 flex w-full max-w-72 flex-col items-center gap-4 rounded-3xl border border-border bg-gray-100 p-4"
      >
        <h4 class="text-lg font-bold">{{ content.data.utilization.heading }}</h4>
        <ul v-if="hasUtilization" class="grid w-full gap-2.5">
          <li
            v-for="item in utilization"
            :key="item.label"
            class="rounded-full border border-border bg-white px-3 py-0.5"
          >
            {{ item.label }}: <strong>{{ item.value ?? '–' }}</strong>
          </li>
        </ul>
        <HpButton
          variant="minimal"
          class="min-h-0 w-auto !text-base !font-semibold"
          @click="show(utilizationSidebarId)"
        >
          {{
            hasUtilization ? content.data.utilization.editLabel : content.data.utilization.addLabel
          }}
        </HpButton>
      </section>
    </aside>

    <template #footer>
      <div class="mx-auto w-full max-w-sm">
        <HpButton class="w-full" @click="hide(id)">Close</HpButton>
      </div>
    </template>
  </HpSidebar>

  <DashboardSidebarUtilization :id="utilizationSidebarId" @hide="onHideUtilization" />
</template>

<script lang="ts" setup>
  import type { QuotedPlanVm } from '~/models/quoting/QuotedPlan'
  import type { QuoteHealthcareCostsSidebarData } from '~/types/cms'
  import { ProfileStore } from '~/stores/profile'
  import { UtilizationSurveyQuestions } from '~/composables/HealthcareUtilization'
  import { HealthcareUtilizationSurveyVModel } from '~/composables/HealthcareUtilizationSurveyVModel'

  const props = defineProps<{
    id: string
    quote: QuotedPlanVm
  }>()

  const { getComponentContent } = Cms.use()
  const { content } = await getComponentContent<QuoteHealthcareCostsSidebarData>(
    'QuoteHealthcareCostsSidebar'
  )

  const { show, hide } = DialogController.use()
  const { quotingConfig } = QuotingConfig.use()
  const profile = ProfileStore.use()

  const benchmark = computed(() => props.quote.benchmarks?.healthcareCosts)

  const utilizationSidebarId = computed(() => `${props.id}-utilization`)

  const { physicianVisitsOpts, specialistVisitsOpts, hospitalVisitsOpts } =
    HealthcareUtilization.use()
  const priCareResp = HealthcareUtilizationSurveyVModel.use(
    UtilizationSurveyQuestions.PhysicianVisits.toString()
  )
  const specResp = HealthcareUtilizationSurveyVModel.use(
    UtilizationSurveyQuestions.SpecialistVisits.toString()
  )
  const hospResp = HealthcareUtilizationSurveyVModel.use(
    UtilizationSurveyQuestions.HospitalAdmissions.toString()
  )

  const hasUtilization = computed(() => !!profile.needsAssessment.healthcareUtilization?.length)

  const utilization = computed(() => [
    {
      label: 'Primary care visits',
      value: physicianVisitsOpts.value.find((x) => x.value == priCareResp.value)?.text
    },
    {
      label: 'Specialist visits',
      value: specialistVisitsOpts.value.find((x) => x.value == specResp.value)?.text
    },
    {
      label: 'Hospital stays',
      value: hospitalVisitsOpts.value.find((x) => x.value == hospResp.value)?.text
    }
  ])

  const { maybePushToSave } = PushToSave.use('add-needs', 'push-to-save-add-needs')
  const { isUtilizationDiff } = QuoteChangeMonitor.use()
  const onHideUtilization = () => {
    if (isUtilizationDiff.value) {
      const isCurrPushedToSave = maybePushToSave()
      if (!isCurrPushedToSave) show('needs-adjustment-engine-run-modal')
    }
  }
</script>

<style scoped></style>
